<!-- Ruta: /vulnerability/custom/logbook/ -->
<script>
	import logbookService from "./service.js";
	import Form from "./_form.svelte";
	import { goto } from "@roxi/routify";
	import { onMount } from "svelte";
	import userService from "../../../user/service.svelte.js";
	import { user } from "../../../user/user.state.svelte.js";
	import { format as t } from "svelte-i18n";
	import { normalizeString } from "../../../../js/utils.js";
	import { Modal } from "bootstrap";
	import * as bootstrap from "bootstrap";

	$goto;

	const redirect = (path, params) => $goto(path, params);

	function unique(arr) {
		return Array.from(new Set(arr));
	}

	const getLogbookExecutors = (logbook) => {
		const keys = (logbook.entriesDetail ?? [])
			.map((e) => e.executorKey)
			.filter(Boolean);

		const names = keys.map((k) => EXECUTORS[k] ?? k);
		return unique(names);
	};

	// Unconditionally removes all stranded backdrop elements and resets body scroll
	const purgeBackdrops = () => {
		document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
		document.body.classList.remove("modal-open");
		document.body.style.removeProperty("padding-right");
		document.body.style.removeProperty("overflow");
	};

	// Safely resolves the Bootstrap Modal class across different bundler import formats
	const getModalClass = () => {
		if (typeof Modal === "function") return Modal;
		if (Modal && typeof Modal.Modal === "function") return Modal.Modal;
		if (Modal && typeof Modal.default === "function") return Modal.default;
		const bs =
			typeof bootstrap !== "undefined" ? bootstrap : window?.bootstrap;
		if (bs) return bs.Modal?.default || bs.Modal || bs.default?.Modal;
		return window?.bootstrap?.Modal || window?.Modal;
	};

	const getModalInstance = (elementId) => {
		const el = document.getElementById(elementId);
		if (!el) return null;

		const ModalClass = getModalClass();
		if (!ModalClass) return null;

		try {
			if (typeof ModalClass.getOrCreateInstance === "function") {
				return ModalClass.getOrCreateInstance(el);
			}
			if (typeof ModalClass === "function") {
				return new ModalClass(el);
			}
		} catch (e) {
			console.warn("Bootstrap instance resolution error:", e);
		}
		return null;
	};

	const openModal = (id) => {
		purgeBackdrops();
		const inst = getModalInstance(id);
		if (inst && typeof inst.show === "function") {
			inst.show();
			return;
		}

		const el = document.getElementById(id);
		if (el) {
			el.classList.add("show");
			el.style.display = "block";
			el.removeAttribute("aria-hidden");
			el.setAttribute("aria-modal", "true");

			if (!document.querySelector(".modal-backdrop")) {
				const backdrop = document.createElement("div");
				backdrop.className = "modal-backdrop fade show";
				document.body.appendChild(backdrop);
			}
			document.body.classList.add("modal-open");
		}
	};

	const closeModal = (id = "logbookModal") => {
		const el = document.getElementById(id);

		try {
			const inst = getModalInstance(id);
			if (inst && typeof inst.hide === "function") {
				inst.hide();
			}
		} catch (e) {
			console.warn("Bootstrap hide error:", e);
		}

		if (el) {
			el.classList.remove("show");
			el.style.display = "none";
			el.setAttribute("aria-hidden", "true");
			el.removeAttribute("aria-modal");
		}

		purgeBackdrops();
		setTimeout(purgeBackdrops, 100);
		setTimeout(purgeBackdrops, 350);
	};

	let logbooks = $state({ data: [] });
	let showArchived = $state(false);
	let searchTerm = $state("");
	let selectedOrder = $state("created_at");

	let openLogbooks = $derived.by(() => {
		return logbooks.data.filter((element) => element.status == "open");
	});

	let closedLogbooks = $derived.by(() => {
		return logbooks.data.filter((element) => element.status == "closed");
	});

	let stats = $derived({
		open: openLogbooks.length,
		closed: closedLogbooks.length,
	});

	function toggleArchivedView() {
		showArchived = !showArchived;
	}

	function goToLogbook(logbookId) {
		console.log(logbookId, "id")
		$goto(`/vulnerability/custom/logbook/[id]`, { id: logbookId });
	}

	async function toggleLogbookStatus(logbookToEdit) {
		if (logbookToEdit.status == "open") {
			let response = await logbookService.close(logbookToEdit.id, {});
			openLogbooks = openLogbooks.filter((l) => l.id != response.id);
			closedLogbooks.push(response);
			closedLogbooks = closedLogbooks.sort(
				(a, b) => new Date(a.created_at) < new Date(b.created_at),
			);
		} else {
			let response = await logbookService.open(logbookToEdit.id, {});
			closedLogbooks = closedLogbooks.filter((l) => l.id != response.id);
			openLogbooks.push(response);
			openLogbooks = openLogbooks.sort(
				(a, b) => new Date(a.created_at) < new Date(b.created_at),
			);
		}
	}

	const sortBy = (event) => {
		selectedOrder = event.target.value;
		if (selectedOrder == "created_at") {
			openLogbooks = [...openLogbooks].sort(
				(a, b) => new Date(a.created_at) - new Date(b.created_at),
			);
		}
		if (selectedOrder == "updated_at") {
			openLogbooks = [...openLogbooks].sort(
				(a, b) => new Date(a.updated_at) - new Date(b.updated_at),
			);
		}
		if (selectedOrder == "name") {
			openLogbooks = [...openLogbooks].sort((a, b) =>
				a.name.localeCompare(b.name, undefined, { numeric: true }),
			);
		}
	};

	const searchLogbooks = () => {
		let currentList = [];
		let searchInput = normalizeString(searchTerm);
		let result = [];
		if (showArchived) {
			currentList = JSON.parse(
				JSON.stringify(
					logbooks.data.filter((l) => l.status == "closed"),
				),
			);
			result = logbooks.data.filter(
				(l) =>
					normalizeString(l.name).includes(searchInput) &&
					l.status == "closed",
			);
			closedLogbooks = searchInput.length == 0 ? currentList : result;
		} else {
			currentList = JSON.parse(
				JSON.stringify(logbooks.data.filter((l) => l.status == "open")),
			);
			result = logbooks.data.filter(
				(l) =>
					normalizeString(l.name).includes(searchInput) &&
					l.status == "open",
			);
			openLogbooks = searchInput.length == 0 ? currentList : result;
		}
	};

	const getData = async () => {
		let pLogbooks = await logbookService.search().catch((e) => e);
		return (logbooks.data = pLogbooks.sort(
			(a, b) => new Date(a.created_at) < new Date(b.created_at),
		));
	};

	onMount(async () => {
		await getData();

		const handleHidden = () => purgeBackdrops();
		document.addEventListener("hidden.bs.modal", handleHidden);

		return () => {
			document.removeEventListener("hidden.bs.modal", handleHidden);
		};
	});
</script>

<section class="content-header">
	<div class="container-fluid">
		<div class="row">
			<div class="col-6">
				<h1 class="mt-3 mb-3 page-title">
					<i class="fas fa-book-dead fa-xs"></i>
					{$t("logbooks.title")}
				</h1>
			</div>
			<div class="col-6"></div>
		</div>
	</div>
</section>

<div class="layout">
	<section class="left">
		<div class="top-controls">
			<div class="actions">
				<button
					class="btn-primary"
					type="button"
					onclick={() => openModal("logbookModal")}
				>
					{$t("logbooks.create_new")}
				</button>

				<button
					class={"btn-toggle " +
						(showArchived ? "btn-toggle--active" : "")}
					aria-pressed={showArchived}
					type="button"
					onclick={toggleArchivedView}
				>
					{showArchived
						? $t("logbooks.show_archived")
						: $t("logbooks.view_archived")}
				</button>
			</div>

			<div class="filters">
				<div class="mt-3">
					<label>{$t("logbooks.search_label")}</label>
					<input
						type="text"
						placeholder={$t("logbooks.search_placeholder")}
						bind:value={searchTerm}
						oninput={searchLogbooks}
						class="input"
					/>
				</div>
			</div>
			<div class="mt-3">
				<label class="d-block">{$t("logbooks.order_label")}</label>
				<select
					onchange={sortBy}
					bind:value={selectedOrder}
					class="select form-control d-inline-block w-25"
				>
					<option value="created_at"
						>{$t("logbooks.order_created")}</option
					>
					<option value="updated_at"
						>{$t("logbooks.order_updated")}</option
					>
					<option value="name">{$t("logbooks.order_name")}</option>
				</select>
			</div>
		</div>

		<div class="section-title">
			<h2 class="h2">
				{showArchived
					? $t("logbooks.list_archived_title")
					: $t("logbooks.list_title")}
			</h2>
			<p class="hint">
				{showArchived
					? $t("logbooks.hint_archived")
					: $t("logbooks.hint_main")}
			</p>
		</div>

		{#key openLogbooks}
			{#if !showArchived}
				{#if openLogbooks.length > 0}
					<div class="grid">
						{#each openLogbooks as logbook (logbook.id)}
							<div class="card">
								<div class="card-head">
									<h3 class="card-title" title={logbook.name}>
										{logbook.name}
									</h3>
									<button
										type="button"
										class={"status " +
											(logbook.status === "open"
												? "status--open"
												: "status--closed")}
										title={$t("logbooks.change_status")}
										onclick={(e) => {
											e.stopPropagation();
											toggleLogbookStatus(logbook);
										}}
									>
										{logbook.status === "open"
											? $t("logbooks.status_open")
											: $t("logbooks.status_closed")}
									</button>
								</div>
								<div class="badge">{logbook.type}</div>
								<p class="meta">
									{$t("logbooks.created_at")}: {logbook.created_at.split(
										"T",
									)[0]}<br />
									{$t("logbooks.updated_at")}: {logbook.updated_at.split(
										"T",
									)[0]}
								</p>
								<button
									type="button"
									class="btn-open"
									onclick={(e) => {
										goToLogbook(logbook.id);
									}}
								>
									{$t("logbooks.open_btn")}
								</button>
							</div>
						{/each}
					</div>
				{:else}
					<div class="empty">
						<h3>{$t("logbooks.empty_title")}</h3>
						<p>
							{showArchived
								? $t("logbooks.empty_archived_desc")
								: $t("logbooks.empty_main_desc")}
						</p>
					</div>
				{/if}
			{:else if closedLogbooks.length > 0}
				<div class="grid">
					{#each closedLogbooks as logbook}
						<div class="card">
							<div class="card-head">
								<h3 class="card-title" title={logbook.name}>
									{logbook.name}
								</h3>
								<button
									type="button"
									class={"status " +
										(logbook.status === "open"
											? "status--open"
											: "status--closed")}
									title={$t("logbooks.change_status")}
									onclick={(e) => {
										e.stopPropagation();
										toggleLogbookStatus(logbook);
									}}
								>
									{logbook.status === "open"
										? $t("logbooks.status_open")
										: $t("logbooks.status_closed")}
								</button>
							</div>
							<div class="badge">{logbook.type}</div>
							<p class="meta">
								{$t("logbooks.created_at")}: {logbook.createdAt}<br
								/>
								{$t("logbooks.updated_at")}: {logbook.lastActivity}
							</p>
							<p class="entries">
								{$t("logbooks.entries_count", {
									values: { count: logbook.entries },
								})}
							</p>
							<p class="executors">
								{$t("logbooks.executors")}:
								{#if getLogbookExecutors(logbook).length === 0}
									—
								{:else}
									{getLogbookExecutors(logbook).join(", ")}
								{/if}
							</p>
							<button
								type="button"
								class="btn-open"
								onclick={(e) => {
									e.stopPropagation();
									goToLogbook(logbook.id);
								}}
							>
								{$t("logbooks.open_btn")}
							</button>
						</div>
					{/each}
				</div>
			{:else}
				<div class="empty">
					<h3>{$t("logbooks.empty_title")}</h3>
					<p>
						{showArchived
							? $t("logbooks.empty_archived_desc")
							: $t("logbooks.empty_main_desc")}
					</p>
				</div>
			{/if}
		{/key}
	</section>

	<aside class="right">
		<div class="sidebar-card">
			<div class="sidebar-title">{$t("logbooks.summary")}</div>
			<div class="stats">
				<div class="stat">
					<div class="stat-num">{stats.open}</div>
					<div class="stat-label">{$t("logbooks.status_open")}</div>
				</div>
				<div class="stat">
					<div class="stat-num">{stats.closed}</div>
					<div class="stat-label">{$t("logbooks.status_closed")}</div>
				</div>
			</div>
		</div>

		<div class="sidebar-card">
			<div class="sidebar-title">{$t("logbooks.recent_projects")}</div>
			{#key openLogbooks}
				{#if openLogbooks.length === 0}
					<div class="sidebar-empty">{$t("logbooks.no_ongoing")}</div>
				{:else}
					<ul class="ongoing-list">
						{#each openLogbooks.slice(0, 10) as p (p.id)}
							<li class="ongoing-item">
								<div class="ongoing-name" title={p.name}>
									<a
										href="#"
										onclick={() => goToLogbook(p.id)}
										>{p.name}</a
									>
								</div>
								<div class="ongoing-meta">
									<span class="ongoing-date"
										>{$t("logbooks.created_at")}: {p.created_at.split(
											"T",
										)[0]}</span
									>
								</div>
							</li>
						{/each}
					</ul>
				{/if}
			{/key}
		</div>
	</aside>
</div>

<div
	class="modal fade"
	id="logbookModal"
	tabindex="-1"
	role="dialog"
	aria-hidden="true"
>
	<div class="modal-dialog modal-dialog-centered" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h5 class="modal-title">{$t("logbooks.modal_title")}</h5>
				<button
					type="button"
					class="btn-close"
					aria-label={$t("logbooks.close")}
					onclick={() => closeModal("logbookModal")}
				></button>
			</div>
			<div class="modal-body">
				<Form
					fetchFunction={getData}
					closeModal={() => closeModal("logbookModal")}
				/>
			</div>
		</div>
	</div>
</div>

<style>
	.page-header {
		margin-bottom: 18px;
	}
	.title {
		font-size: 28px;
		font-weight: 700;
		margin: 0;
	}
	.subtitle {
		color: #666;
		margin: 4px 0 0 0;
	}
	.layout {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 360px;
		gap: 18px;
		align-items: start;
	}
	@media (max-width: 1200px) {
		.layout {
			grid-template-columns: 1fr;
		}
		.right {
			order: 2;
		}
		.left {
			order: 1;
		}
	}
	.top-controls {
		border: 1px solid rgba(0, 0, 0, 0.06);
		border-radius: 12px;
		background: #fff;
		padding: 14px;
	}
	.actions {
		display: flex;
		gap: 10px;
		justify-content: flex-start;
		flex-wrap: wrap;
		margin-bottom: 12px;
	}
	.filters {
		display: grid;
		grid-template-columns: 1.2fr 1fr 1fr;
		gap: 10px;
		align-items: center;
	}
	@media (max-width: 900px) {
		.filters {
			grid-template-columns: 1fr;
		}
	}
	.input,
	.select {
		width: 100%;
		padding: 8px;
		border-radius: 8px;
		border: 1px solid #ccc;
	}
	.btn-primary {
		background: #007bff;
		color: #fff;
		padding: 10px 14px;
		border: none;
		border-radius: 8px;
		cursor: pointer;
	}
	.btn-secondary {
		background: #e6e6e6;
		padding: 10px 14px;
		border: none;
		border-radius: 8px;
		cursor: pointer;
	}
	.btn-toggle {
		background: #f1f3f5;
		border: 1px solid rgba(0, 0, 0, 0.08);
		padding: 10px 14px;
		border-radius: 8px;
		cursor: pointer;
		transition: 0.15s;
	}
	.btn-toggle--active {
		background: #111827;
		color: #fff;
		border-color: #111827;
	}
	.section-title {
		margin: 14px 2px 10px 2px;
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 12px;
	}
	.h2 {
		font-size: 18px;
		font-weight: 700;
		margin: 0;
	}
	.hint {
		margin: 0;
		color: #6b7280;
		font-size: 13px;
	}
	.grid {
		display: grid;
		gap: 12px;
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
	@media (max-width: 1400px) {
		.grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	@media (max-width: 900px) {
		.grid {
			grid-template-columns: 1fr;
		}
	}
	.card {
		border: 1px solid rgba(0, 0, 0, 0.08);
		padding: 14px;
		border-radius: 12px;
		background: #fff;
		min-width: 0;
	}
	.card:hover {
		border-color: #007bff;
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
	}
	.card-head {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 10px;
		margin-bottom: 6px;
	}
	.card-title {
		font-size: 16px;
		font-weight: 700;
		margin: 0;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.status {
		font-size: 12px;
		padding: 4px 8px;
		border-radius: 999px;
		white-space: nowrap;
		border: 1px solid rgba(0, 0, 0, 0.08);
		background: transparent;
		cursor: pointer;
		line-height: 1.1;
		transition: 0.12s;
	}
	.status:focus {
		outline: 2px solid rgba(0, 123, 255, 0.35);
		outline-offset: 2px;
	}
	.status--open {
		background: #ecfeff;
		color: #0f766e;
	}
	.status--closed {
		background: #f3f4f6;
		color: #374151;
	}
	.status:hover {
		filter: brightness(0.98);
	}
	.badge {
		display: inline-block;
		width: 100%;
		text-align: center;
		background: #eef;
		padding: 5px 8px;
		border-radius: 8px;
		font-size: 12px;
		margin: 8px 0 10px 0;
	}
	.meta {
		color: #666;
		font-size: 13px;
		margin: 0;
	}
	.entries {
		color: #111827;
		font-weight: 700;
		margin: 10px 0 6px 0;
	}
	.executors {
		color: #4b5563;
		font-size: 13px;
		margin: 0;
	}
	.btn-open {
		margin-top: 10px;
		width: 100%;
		padding: 8px 10px;
		background: #007bff;
		color: white;
		border: none;
		border-radius: 8px;
		cursor: pointer;
	}
	.empty {
		border: 1px dashed rgba(0, 0, 0, 0.15);
		border-radius: 12px;
		padding: 18px;
		background: #fff;
	}
	.sidebar-card {
		border: 1px solid rgba(0, 0, 0, 0.06);
		border-radius: 12px;
		background: #fff;
		padding: 14px;
		margin-bottom: 12px;
	}
	.sidebar-title {
		font-weight: 800;
		margin-bottom: 8px;
	}
	.sidebar-hint {
		margin: 0 0 10px 0;
		color: #6b7280;
		font-size: 13px;
	}
	.stats {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 10px;
		margin-top: 10px;
	}
	.stat {
		border: 1px solid rgba(0, 0, 0, 0.06);
		border-radius: 12px;
		padding: 10px;
		text-align: center;
		background: #f9fafb;
	}
	.stat-num {
		font-size: 18px;
		font-weight: 900;
		color: #111827;
	}
	.stat-label {
		font-size: 12px;
		color: #6b7280;
	}
	.ongoing-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}
	.ongoing-item {
		border: 1px solid rgba(0, 0, 0, 0.06);
		border-radius: 12px;
		padding: 10px;
		background: #fff;
	}
	.ongoing-name {
		font-weight: 800;
		font-size: 13px;
		color: #111827;
		margin-bottom: 4px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.ongoing-meta {
		font-size: 12px;
		color: #6b7280;
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		align-items: center;
	}
	.ongoing-date {
		font-weight: 700;
	}
	.ongoing-sep {
		opacity: 0.6;
	}
	.sidebar-empty {
		color: #6b7280;
		font-size: 13px;
		border: 1px dashed rgba(0, 0, 0, 0.12);
		border-radius: 12px;
		padding: 10px;
		background: #fff;
	}
	.help {
		color: #6b7280;
		font-size: 12px;
	}
</style>
